import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { ErrorType } from "@/types";
import { useDeleteConsultationMutation } from "@/services/consultationApi";
import { useGetCoachListQuery } from "@/services/coachApi";
import { DataTableRowActions } from "./data-table-row-actions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetConsultationsQuery,
  useUpdateConsultationMutation,
  usePatchConsultationStatusMutation,
} from "@/services/consultationApi";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import ConsultationForm from "../modal/form";
import GoalEvaluationSheet from "../modal/steps/evaluation";
import { Consultation } from "@/types/consultation";
import TableFilters from "@/components/ui/table/data-table-filter";
import { displayValue, formatDate, generateQueryString } from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { toast } from "@/components/ui/use-toast";
import { ConsultationStatus } from "@/types/enums";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  status?: string;
  type?: string;
  coach_ids?: number[];
  [key: string]: string | number | (string | number)[] | null | undefined;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export default function ConsultationTableView() {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [updateConsultation] = useUpdateConsultationMutation();
  const [patchConsultationStatus] = usePatchConsultationStatusMutation();

  const consultations = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .consultations ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData<SearchCriteriaType>(initialValue);

  const {
    data: consultationsData,
    isLoading,
    refetch,
  } = useGetConsultationsQuery(
    {
      query: query || generateQueryString(initialValue),
      orgId: orgId,
    }
  );

  // Coach list for filter dropdown
  const { data: coachList } = useGetCoachListQuery(orgId, { skip: !orgId });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<Consultation | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [isEvaluationOpen, setIsEvaluationOpen] = useState(false);
  const [evaluationData, setEvaluationData] = useState<Consultation | undefined>(undefined);

  const handleGoalEvaluation = (data: Consultation) => {
    setEvaluationData(data);
    setIsEvaluationOpen(true);
  };

  const consultationTableData = React.useMemo(() => {
    return Array.isArray(consultationsData?.data)
      ? consultationsData?.data
      : [];
  }, [consultationsData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [deleteConsultation, { isLoading: deleteLoading }] =
    useDeleteConsultationMutation();

  const handleAddConsultation = () => {
    setIsDialogOpen(true);
    setAction("add");
    setFormData(undefined);
  };

  const handleEditConsultation = (data: Consultation) => {
    setIsDialogOpen(true);
    setAction("edit");
    setFormData(data);
  };


  const handleDeleteConsultation = async (data: Consultation) => {
    try {
      const resp = await deleteConsultation({ id: data.id as number, org_id: orgId }).unwrap();
      if (resp) {
        if (
          consultationsData?.filtered_counts == 1 &&
          searchCriteria.offset > 0
        ) {
          setSearchCriteria((prev) => ({
            ...prev,
            offset: prev.offset - 1,
          }));
          setFilterData((prev) => ({ ...prev, offset: prev.offset - 1 }));
        }
        setInputValue("");
        refetch();
        toast({
          variant: "success",
          title: "Consultation Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message
          }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<Consultation> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={consultations}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditConsultation}
        handleDelete={handleDeleteConsultation}
        handleGoalEvaluation={handleGoalEvaluation}
      />
    ),
  };

  const columns: ColumnDef<Consultation>[] = [
    {
      accessorKey: "member_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.member_name}`.length > 20
                          ? `${row.original.member_name}`.substring(0, 20) +
                              "..."
                          : `${row.original.member_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="normal-case text-sm w-fit ">
                    {displayValue(`${row?.original?.member_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "own_member_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Own Id"
          sortKey="own_member_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.own_member_id}`.length > 20
                          ? `${row.original.own_member_id}`.substring(0, 20) +
                              "..."
                          : `${row.original.own_member_id}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="normal-case text-sm w-fit ">
                    {displayValue(`${row?.original?.own_member_id}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "coach_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Assigned Coach"
          sortKey="coach_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.coach_name}`.length > 20
                          ? `${row.original.coach_name}`.substring(0, 20) +
                              "..."
                          : `${row.original.coach_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="normal-case text-sm w-fit ">
                    {displayValue(`${row?.original?.coach_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "consultation_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Date"
          sortKey="date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{formatDate(row.original.consultation_date) || "-"}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original?.status;
        return (
          <div className="flex gap-2 items-center">
            <span
              className={`${
                status == ConsultationStatus.Approved
                  ? "bg-green-500"
                  : status == ConsultationStatus.Scheduled
                  ? "bg-blue-500"
                  : status == ConsultationStatus.Rejected
                  ? "bg-red-500"
                  : status == ConsultationStatus.Draft
                  ? "bg-gray-500"
                  : status == ConsultationStatus.AwaitingApproval
                  ? "bg-orange-400"
                  : "bg-yellow-500"
              } rounded-[50%] w-3 h-3`}
            ></span>
            <span className="capitalize text-sm font-medium">
              {status == ConsultationStatus.Approved
                ? "Approved"
                : status == ConsultationStatus.Scheduled
                ? "Scheduled"
                : status == ConsultationStatus.Rejected
                ? "Rejected"
                : status == ConsultationStatus.Draft
                ? "Draft"
                : status == ConsultationStatus.AwaitingApproval
                ? "Awaiting Approval"
                : "Pending"}
            </span>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    ...(consultations !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: consultationTableData as Consultation[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = consultationsData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    // Update the filter state based on field and value
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: ConsultationStatus.Approved, name: "Approved" },
        { id: ConsultationStatus.AwaitingApproval, name: "Awaiting Approval" },
        { id: ConsultationStatus.Draft, name: "Draft" },
        { id: ConsultationStatus.Rejected, name: "Rejected" }
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "coach_ids",
      label: "Coach",
      options: coachList?.map((c) => ({
        value: c.value,        // keep as number — matches defaultValue type
        label: c.label,
      })) ?? [],
      function: (selectedValues: (string | number)[]) =>
        handleFilterChange("coach_ids", selectedValues.map(Number)),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row gap-2 items-center px-3">
        <div className="flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>
        <div className="flex flex-row gap-2 flex-shrink-0">
          <Button
            access={consultations !== "read"}
            className="bg-primary text-sm text-black flex items-center gap-1 h-8 w-8 p-0 sm:w-auto sm:px-2 flex-shrink-0"
            onClick={handleAddConsultation}
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Create New</span>
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center flex-shrink-0 ${
              Object.keys(filterData).length ? "border-primary" : ""
            }`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden px-3 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="flex space-x-2 items-center">
              <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="size-2 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="size-2 bg-black rounded-full animate-bounce"></div>
            </div>
          </div>
        ) : consultationTableData.length > 0 ? (
          consultationTableData.map((item) => {
            const status = item.status;
            const statusColor =
              status === ConsultationStatus.Approved ? "bg-green-500" :
              status === ConsultationStatus.Scheduled ? "bg-blue-500" :
              status === ConsultationStatus.Rejected ? "bg-red-500" :
              status === ConsultationStatus.Draft ? "bg-gray-500" :
              status === ConsultationStatus.AwaitingApproval ? "bg-orange-400" :
              "bg-yellow-500";
            const statusLabel =
              status === ConsultationStatus.Approved ? "Approved" :
              status === ConsultationStatus.Scheduled ? "Scheduled" :
              status === ConsultationStatus.Rejected ? "Rejected" :
              status === ConsultationStatus.Draft ? "Draft" :
              status === ConsultationStatus.AwaitingApproval ? "Awaiting Approval" :
              "Pending";

            return (
              <div
                key={item.id}
                className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2 ${
                  consultations !== "read" ? "cursor-pointer active:bg-gray-50" : ""
                }`}
                onClick={() => consultations !== "read" && handleEditConsultation(item as Consultation)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-gray-900 truncate">{item.member_name || "—"}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.own_member_id || "—"}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`w-2 h-2 rounded-full ${statusColor}`}></span>
                    <span className="text-xs font-medium text-gray-700">{statusLabel}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span><span className="font-medium text-gray-700">Coach:</span> {item.coach_name || "—"}</span>
                  <span><span className="font-medium text-gray-700">Date:</span> {formatDate(item.consultation_date) || "—"}</span>
                </div>
                {consultations !== "read" && (
                  <div className="pt-1 flex justify-end">
                    <DataTableRowActions
                      access={consultations}
                      data={item as Consultation}
                      refetch={refetch}
                      handleEdit={handleEditConsultation}
                      handleDelete={handleDeleteConsultation}
                    />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-center text-sm text-gray-400 py-8">No records found.</p>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        consultations !== "read" &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest("td"))
                      ) {
                        handleEditConsultation(
                          row.original as unknown as Consultation
                        );
                      }
                    }}
                    className={
                      consultations !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : consultationTableData.length > 0 ? (
                <TableRow className="!p-0">
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No consultation found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {consultationTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <ConsultationForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
      />

      {evaluationData && (
        <GoalEvaluationSheet
          isOpen={isEvaluationOpen}
          onOpenChange={setIsEvaluationOpen}
          data={evaluationData}
        />
      )}
    </div>
  );
}
